import { Platform } from '@reelplicate/api-client/model';
import { z } from 'zod';

/**
 * Onboarding forms. Client-side only until onboarding lands in the API;
 * then these move to the generated schemas. Step 1 starts empty; the later
 * steps still open with the Figma sample values (`SAMPLE_*`) until their
 * answers are stored.
 */

// A bare domain is fine ("oatandember.com"); a scheme and path are optional.
const WEBSITE = /^(https?:\/\/)?[\w-]+(\.[\w-]+)+(\/\S*)?$/i;
export const website = (message: string) =>
  z
    .string()
    .trim()
    .refine((v) => v === '' || WEBSITE.test(v), message);

// Step 1 · Workspace
export const ROLES = ['founder', 'marketer', 'agency', 'creator'] as const;
export const TEAM_SIZES = ['1', '2-5', '6-20', '21-50', '50+'] as const;

export const WorkspaceStepSchema = z.object({
  name: z.string().trim().min(1, 'Name your workspace'),
  website: website('Enter a website like oatandember.com'),
  role: z.enum(ROLES, 'Choose what best describes you'),
  teamSize: z.enum(TEAM_SIZES, 'Choose your team size'),
});
export type WorkspaceStepValues = z.infer<typeof WorkspaceStepSchema>;

// Step 2 · Brand
export const BrandStepSchema = z.object({
  product: z.string().trim().min(1, 'Name your brand or product'),
  audience: z.string().trim().min(1, 'Describe who you sell to'),
  offer: z.string().trim(),
  tone: z.array(z.string()).min(1, 'Add at least one word for your tone'),
  approvedClaims: z.array(z.string()),
  prohibited: z.array(z.string()),
});
export type BrandStepValues = z.infer<typeof BrandStepSchema>;

// Step 3 · Competitors
/** A TikTok / Instagram handle as typed: optional, checked again by the API. */
export const handle = z
  .string()
  .trim()
  .regex(
    /^(@?[\w.]{1,30}|https?:\/\/\S+|(www\.)?(tiktok|instagram)\.com\/\S+)?$/i,
    'Use @handle or a profile link',
  );

// Step 4 · Platforms (the API's values, as generated)
export const PlatformsStepSchema = z.object({
  platforms: z.array(z.enum(Platform)).min(1, 'Pick at least one platform'),
});
export type PlatformsStepValues = z.infer<typeof PlatformsStepSchema>;

export const SAMPLE_PLATFORMS: PlatformsStepValues = {
  platforms: ['TIKTOK', 'INSTAGRAM'],
};

// Step 6 · Team
export const TEAM_ROLES = ['editor', 'reviewer'] as const;

export const TeamStepSchema = z.object({
  emails: z.array(z.email()),
  role: z.enum(TEAM_ROLES),
});
export type TeamStepValues = z.infer<typeof TeamStepSchema>;

export const isEmail = (value: string) => z.email().safeParse(value).success;
